import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from '../../assets/styles/Homepage/Testimonials.module.css';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';

import 'swiper/css';

import user1 from '../../assets/images/yogateacher1.jpg';
import user2 from '../../assets/images/yogateacher2.jpg';
import user3 from '../../assets/images/yogateacher3.jpg';
import user4 from '../../assets/images/yoogateacher4.webp';

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const FALLBACK_HEADING = {
  mainHeading: 'Loved by Our Community',
  subHeading:
    'Thousands of people around the world trust Hatha Yog for mindful movement and inner balance.',
};

const FALLBACK_TESTIMONIALS = [
  {
    name: 'Ananya Sharma',
    location: 'Delhi, India',
    image: user1,
    quote:
      'Hatha Yog transformed not just my body, but my mindset. Every session feels intentional and deeply calming.',
    rating: 5,
  },
  {
    name: 'Rohit Mehta',
    location: 'Mumbai, India',
    image: user2,
    quote:
      'The experience feels premium and personal. The teachers guide you gently yet powerfully through every practice.',
    rating: 5,
  },
  {
    name: 'Sophia Williams',
    location: 'London, UK',
    image: user3,
    quote:
      'This platform brings stillness into my everyday life. It is elegant, thoughtful, and incredibly grounding.',
    rating: 5,
  },
  {
    name: 'Daniel Cooper',
    location: 'New York, USA',
    image: user4,
    quote:
      'Beautifully designed sessions that meet you exactly where you are. This has become my daily ritual.',
    rating: 5,
  },
];

const resolveAssetUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/uploads/')) return `${ENV_API_BASE_URL}${value}`;
  if (value.startsWith('uploads/')) return `${ENV_API_BASE_URL}/${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const cardVariants = {
  hidden: {
    opacity: 0,
    scale: 0.7,
    rotateX: 40,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotateX: 0,
    transition: {
      duration: 0.9,
      ease: 'easeOut',
    },
  },
};

const avatarPop = {
  hidden: { scale: 0 },
  visible: {
    scale: 1,
    transition: { delay: 0.4, type: 'spring', stiffness: 200 },
  },
};

const textSlide = {
  hidden: { x: -80, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { delay: 0.5, duration: 0.7 },
  },
};

const Testimonials = () => {
  const [heading, setHeading] = useState(FALLBACK_HEADING);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);

  useEffect(() => {
    let isMounted = true;

    const fetchTestimonials = async () => {
      try {
        const [headingRes, testimonialsRes] = await Promise.all([
          axios.get(`${ENV_API_BASE_URL}/api/testimonial-heading`),
          axios.get(`${ENV_API_BASE_URL}/api/testimonials`),
        ]);

        if (!isMounted) return;

        const headingData = Array.isArray(headingRes.data?.data)
          ? headingRes.data.data[0]
          : null;
        if (headingData?.mainHeading || headingData?.subHeading) {
          setHeading({
            mainHeading: headingData.mainHeading || FALLBACK_HEADING.mainHeading,
            subHeading: headingData.subHeading || FALLBACK_HEADING.subHeading,
          });
        }

        const cards = Array.isArray(testimonialsRes.data?.data)
          ? testimonialsRes.data.data.filter((item) => item?.isActive !== false)
          : [];

        if (cards.length) {
          setTestimonials(
            cards.map((item) => ({
              ...item,
              image: resolveAssetUrl(item.image) || user1,
              rating: Number(item.rating || 5),
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load homepage testimonials:', error);
      }
    };

    fetchTestimonials();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className={styles.section}>
      <motion.div
        className={styles.glow}
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      <div className="container text-center">
        <motion.h2
          className={styles.heading}
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {heading.mainHeading}
        </motion.h2>

        <motion.p
          className={styles.subText}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          viewport={{ once: true }}
        >
          {heading.subHeading}
        </motion.p>

        <Swiper
          modules={[Autoplay]}
          autoplay={{ delay: 3500, disableOnInteraction: false }}
          loop={testimonials.length > 1}
          slidesPerView={1}
          className={styles.swiper}
        >
          {testimonials.map((item, index) => (
            <SwiperSlide key={item._id || `${item.name}-${index}`}>
              <motion.div
                className={styles.card}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div
                  className={styles.avatarWrapper}
                  variants={avatarPop}
                  initial="hidden"
                  animate="visible"
                >
                  <img src={item.image} alt={item.name} />
                </motion.div>

                <motion.p
                  className={styles.quote}
                  variants={textSlide}
                  initial="hidden"
                  animate="visible"
                >
                  {item.quote}
                </motion.p>

                <div className={styles.stars}>{'★'.repeat(Math.max(1, Math.min(5, Number(item.rating || 5))))}</div>

                <h5 className={styles.name}>{item.name}</h5>
                <span className={styles.location}>{item.location}</span>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default Testimonials;
